import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar, Clock, Tag, User, MessageCircle, Share2 } from "lucide-react";
import { BLOG_POSTS } from "@/lib/blog-data";
import { INSTITUTE } from "@/lib/content";
import useSEO from "@/hooks/useSEO";

function formatDate(d) {
  return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
}

function renderBlock(b, i) {
  switch (b.type) {
    case "h2":
      return (
        <h2 key={i} className="font-display text-2xl sm:text-3xl font-black tracking-tight text-gray-950 mt-12 mb-4">
          {b.text}
        </h2>
      );
    case "h3":
      return (
        <h3 key={i} className="font-display text-xl sm:text-2xl font-bold tracking-tight text-gray-950 mt-8 mb-3">
          {b.text}
        </h3>
      );
    case "ul":
      return (
        <ul key={i} className="space-y-2.5 my-6 pl-1">
          {b.items.map((it, j) => (
            <li key={j} className="flex gap-3 text-gray-800 leading-relaxed">
              <span className="w-1.5 h-1.5 bg-red-600 rounded-full mt-2.5 shrink-0" />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol key={i} className="space-y-3 my-6 pl-1 list-none counter-reset-ol">
          {b.items.map((it, j) => (
            <li key={j} className="flex gap-4 text-gray-800 leading-relaxed">
              <span className="font-display font-black text-red-600 text-lg leading-none shrink-0 w-7">
                {String(j + 1).padStart(2, "0")}
              </span>
              <span>{it}</span>
            </li>
          ))}
        </ol>
      );
    case "quote":
      return (
        <blockquote key={i} className="my-8 border-l-4 border-red-600 pl-6 py-3 bg-gray-50">
          <p className="font-display text-xl lg:text-2xl italic tracking-tight text-gray-950 leading-snug">
            "{b.text}"
          </p>
          {b.cite && <cite className="not-italic block text-xs uppercase tracking-widest text-red-600 mt-3 font-bold">{b.cite}</cite>}
        </blockquote>
      );
    case "p":
    default:
      return <p key={i} className="text-gray-700 leading-relaxed my-5 text-base lg:text-[17px]">{b.text}</p>;
  }
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  useSEO({
    title: post?.metaTitle || post?.title,
    description: post?.metaDescription || post?.excerpt,
    canonical: `https://www.kothariclasses.in/blog/${slug}`,
    image: post?.image,
  });

  if (!post) return <Navigate to="/blog" replace />;

  const idx = BLOG_POSTS.findIndex((p) => p.slug === slug);
  const prev = BLOG_POSTS[idx - 1];
  const next = BLOG_POSTS[idx + 1];
  const related = BLOG_POSTS.filter((p) => p.slug !== slug && p.category === post.category).slice(0, 3);

  const share = () => {
    const url = `https://www.kothariclasses.in/blog/${slug}`;
    if (navigator.share) {
      navigator.share({ title: post.title, url }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(url);
    }
  };

  return (
    <div data-testid="blog-post-page">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 text-sm">
          <Link to="/blog" className="text-gray-600 hover:text-red-600 inline-flex items-center gap-1" data-testid="blog-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>
        </div>
      </div>

      {/* Title block */}
      <section className="bg-white pt-10 pb-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-gray-500 mb-5">
            <span className="inline-flex items-center gap-1.5 text-red-600"><Tag className="w-3.5 h-3.5" />{post.category}</span>
            <span className="w-1 h-1 bg-gray-400 rounded-full" />
            <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{formatDate(post.date)}</span>
            <span className="w-1 h-1 bg-gray-400 rounded-full" />
            <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{post.read}</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-gray-950 leading-[1.05]">
            {post.title}
          </h1>
          <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-600 text-white flex items-center justify-center font-display font-black">KC</div>
              <div>
                <div className="font-display font-bold text-gray-950 text-sm flex items-center gap-1"><User className="w-3.5 h-3.5" /> {post.author}</div>
                <div className="text-xs text-gray-500">Jalgaon · Baliram Peth</div>
              </div>
            </div>
            <button onClick={share} className="inline-flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-red-600" data-testid="blog-share-button">
              <Share2 className="w-4 h-4" /> Share
            </button>
          </div>
        </div>
      </section>

      {/* Featured image */}
      <section className="bg-white pb-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            <div className="absolute -top-3 -left-3 w-full h-full border-2 border-red-600 hidden sm:block" />
            <img src={post.image} alt={post.title} className="relative w-full h-64 sm:h-96 object-cover" />
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="bg-white pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 prose-custom">
          {post.blocks.map(renderBlock)}
        </div>
      </section>

      {/* CTA strip */}
      <section className="bg-red-600 text-white py-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="blog-cta">
          <div>
            <div className="font-display text-xl sm:text-2xl font-black leading-tight">Want help applying this to your syllabus?</div>
            <div className="text-sm text-white/90">Book a free demo at Kothari Classes, Jalgaon — our faculty will guide you personally.</div>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Link to="/admissions" className="bg-white text-red-600 font-semibold px-5 py-3 hover:bg-gray-100 transition-colors inline-flex items-center gap-2 text-sm" data-testid="blog-cta-demo">
              Book Free Demo <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={`https://wa.me/${INSTITUTE.whatsapp_number}?text=${encodeURIComponent("Hi, I read your blog on " + post.title + " and would like to know more.")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="border border-white text-white font-semibold px-5 py-3 hover:bg-white hover:text-red-600 transition-colors inline-flex items-center gap-2 text-sm"
              data-testid="blog-cta-whatsapp"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Related / next-prev */}
      <section className="bg-gray-50 py-14">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 gap-4 mb-10">
            {prev ? (
              <Link to={`/blog/${prev.slug}`} className="border border-gray-200 bg-white p-5 card-lift group" data-testid="blog-prev">
                <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500 flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Previous</div>
                <div className="font-display font-bold text-gray-950 mt-2 group-hover:text-red-600">{prev.title}</div>
              </Link>
            ) : <div />}
            {next ? (
              <Link to={`/blog/${next.slug}`} className="border border-gray-200 bg-white p-5 card-lift group text-right" data-testid="blog-next">
                <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500 flex items-center justify-end gap-1">Next <ArrowRight className="w-3 h-3" /></div>
                <div className="font-display font-bold text-gray-950 mt-2 group-hover:text-red-600">{next.title}</div>
              </Link>
            ) : <div />}
          </div>

          {related.length > 0 && (
            <>
              <h3 className="font-display text-2xl font-black tracking-tight text-gray-950 mb-5">Related articles</h3>
              <div className="grid sm:grid-cols-3 gap-4">
                {related.map((r) => (
                  <Link key={r.slug} to={`/blog/${r.slug}`} className="bg-white border border-gray-200 p-5 card-lift group" data-testid={`blog-related-${r.slug}`}>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-red-600">{r.category}</div>
                    <div className="font-display font-bold text-gray-950 mt-2 group-hover:text-red-600 leading-snug">{r.title}</div>
                    <div className="text-xs text-gray-500 mt-3 flex items-center gap-1.5"><Calendar className="w-3 h-3" />{formatDate(r.date)}</div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
